import { ChevronUp } from "@roman/shared/components/Icons";
import { BACKGROUND_MULTIPLE_COLORS, MULTIPLE_COLORS, YES_NO_COLORS } from "@roman/shared/constants/predictionChart";
import { formatNumberWithCommas } from "@roman/shared/utils/formatNumberWithCommas";
import {
  PredictionMarketType,
  getPredictionMarketType,
  getYesNoChoices,
} from "@roman/shared/utils/predictionMarket/utils";
import { useState } from "react";

type EntryChoice = {
  choiceName: string;
  freePoints: number;
  choiceId: string;
};

type EntryPointsDropdownProps = {
  entryChoices: EntryChoice[];
  choices: { id: string; name: string }[]; // All available choices to determine color index
};

export const EntryPointsDropdown = ({ entryChoices, choices }: EntryPointsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Detect if this is a Yes/No market
  const predictionMarketType = getPredictionMarketType(choices.map((c) => ({ name: c.name })));
  const isYesNoMarket = predictionMarketType === PredictionMarketType.TwoChoice;

  // Helper function to get color for a choice
  const getChoiceColors = (choiceId: string) => {
    if (isYesNoMarket) {
      const { yesChoice } = getYesNoChoices(
        choices.map((c) => ({
          id: c.id,
          name: c.name,
          probability: 0.5, // dummy probability for getYesNoChoices
        })),
      );

      const isYesChoice = choiceId === yesChoice.id;
      return {
        color: isYesChoice ? YES_NO_COLORS[0] : YES_NO_COLORS[1], // Yes: #00AAA1, No: #FF5A37
        backgroundColor: isYesChoice ? "#E5FFF3" : "#FFE4D9", // Light versions
      };
    }

    // Multiple choice market - use existing logic
    const choiceIndex = choices.findIndex((choice) => choice.id === choiceId);
    const colorIndex = choiceIndex >= 0 ? choiceIndex : 0;
    return {
      color: MULTIPLE_COLORS[colorIndex % MULTIPLE_COLORS.length],
      backgroundColor: BACKGROUND_MULTIPLE_COLORS[colorIndex % BACKGROUND_MULTIPLE_COLORS.length],
    };
  };

  // Aggregate entries by choiceId
  const aggregatedEntries = entryChoices.reduce((acc, entry) => {
    const existingEntry = acc.find((item) => item.choiceId === entry.choiceId);
    if (existingEntry) {
      existingEntry.freePoints += entry.freePoints;
    } else {
      acc.push({ ...entry });
    }
    return acc;
  }, [] as EntryChoice[]);

  if (aggregatedEntries.length === 0) {
    return null;
  }

  // Single entry - display directly
  if (aggregatedEntries.length === 1) {
    const entry = aggregatedEntries[0];
    const { color, backgroundColor } = getChoiceColors(entry.choiceId);
    return (
      <span
        className="flex items-center text-[10px] font-medium px-[6px] h-[16px] justify-between rounded-[2px]"
        style={{ color, backgroundColor }}
        title={`${entry.choiceName}: ${entry.freePoints}pt`}
      >
        <span className="truncate max-w-[80px]">
          <span>{entry.choiceName}</span>
          {isYesNoMarket && <span>派</span>}
        </span>

        <span className="ml-[12px] flex-shrink-0">{formatNumberWithCommas(entry.freePoints)}pt</span>
      </span>
    );
  }

  // Multiple entries - display dropdown
  // Find the entry with the highest points
  const maxEntry = aggregatedEntries.reduce((max, entry) => (entry.freePoints > max.freePoints ? entry : max));
  const maxEntryColors = getChoiceColors(maxEntry.choiceId);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center text-[10px] font-medium px-[6px] h-[16px] justify-between rounded-[2px] active:scale-[0.97] transition-transform duration-100"
        style={{
          color: maxEntryColors.color,
          backgroundColor: maxEntryColors.backgroundColor,
        }}
        title={`${maxEntry.choiceName} ${formatNumberWithCommas(maxEntry.freePoints)}pt`}
      >
        <span className="flex items-center w-full justify-between min-w-0">
          <span className="truncate max-w-[80px]">
            <span>{maxEntry.choiceName}</span>
            {isYesNoMarket && <span>派</span>}
          </span>
          <span className="ml-[12px] flex-shrink-0">{formatNumberWithCommas(maxEntry.freePoints)}pt</span>
        </span>
        <ChevronUp className={"w-[8px] h-[4.67px] ml-[4px] transition-transform rotate-180 flex-shrink-0"} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          {/* Dropdown content */}
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-[2px] shadow-[0px_1px_4px_0px_rgba(0,0,0,0.10)] z-20 animate-in fade-in-0 duration-200">
            <div className="p-[3px] gap-[2px] flex flex-col">
              {aggregatedEntries
                .sort((a, b) => b.freePoints - a.freePoints)
                .map((entry, index) => {
                  const { color, backgroundColor } = getChoiceColors(entry.choiceId);
                  return (
                    <div
                      key={index}
                      className="px-[9px] text-[10px] font-medium hover:bg-gray-50 flex justify-between items-center h-[16px] rounded-[2px]"
                      style={{ color, backgroundColor }}
                    >
                      <span className="truncate max-w-[80px]">
                        <span>{entry.choiceName}</span>
                        {isYesNoMarket && <span>派</span>}
                      </span>
                      <span className={"ml-[12px] flex-shrink-0"} style={{ color, backgroundColor }}>
                        {formatNumberWithCommas(entry.freePoints)}pt
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
